import { useCallback, useEffect, useState } from "react";

import { PaymentController } from "@icg360/payment-toolkit-v2";

import { usePolicyDetail, useUser } from "data/ss-data-hooks";
import {
  type PaymentConfig,
  acceptPaymentOnCancelledPolicy,
  addDays,
  formatPhone,
  getBillingAddressRaw,
  isFormattedPhone,
  payControl,
} from "utils";

type PaymentButtonHookParams = {
  paymentConfig?: PaymentConfig;
  onPaymentError?: (error: Error) => void;
  onPaymentCanceled?: () => void;
  onPaymentComplete?: (response) => void;
  onPaymentStart?: () => void;
  onScheduledPaymentCreated?: (response) => void;
};

export const usePaymentButton = ({
  paymentConfig,
  onPaymentStart,
  onPaymentCanceled,
  onPaymentComplete,
  onPaymentError,
  onScheduledPaymentCreated,
}: PaymentButtonHookParams) => {
  const defaultPaymentConfig = usePaymentConfig();
  const payment = paymentConfig ?? defaultPaymentConfig;

  const [oneIncPortalReady, setIsOneIncPortalReady] = useState(
    payControl?.isReady() ?? false
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onPaymentReady = () => {
      setIsOneIncPortalReady(true);
    };
    payControl?.addEventListener(
      PaymentController.PAYMENT_READY,
      onPaymentReady
    );
    return () =>
      payControl?.removeEventListener(
        PaymentController.PAYMENT_READY,
        onPaymentReady
      );
  }, []);

  useEffect(() => {
    const eventHandler = () => {
      setLoading(false);
      onPaymentStart?.();
    };
    payControl?.addEventListener(PaymentController.PAYMENT_START, eventHandler);
    return () =>
      payControl?.removeEventListener(
        PaymentController.PAYMENT_START,
        eventHandler
      );
  }, [onPaymentStart]);

  useEffect(() => {
    const eventHandler = () => {
      setLoading(false);
      onPaymentCanceled?.();
    };
    payControl?.addEventListener(
      PaymentController.PAYMENT_CANCEL,
      eventHandler
    );
    return () =>
      payControl?.removeEventListener(
        PaymentController.PAYMENT_CANCEL,
        eventHandler
      );
  }, [onPaymentCanceled]);

  useEffect(() => {
    const eventHandler = (error) => {
      setLoading(false);
      onPaymentError?.(error);
    };
    payControl?.addEventListener(PaymentController.PAYMENT_ERROR, eventHandler);
    return () =>
      payControl?.removeEventListener(
        PaymentController.PAYMENT_ERROR,
        eventHandler
      );
  }, [onPaymentError]);

  useEffect(() => {
    const eventHandler = (response) => {
      onPaymentComplete?.(response);
    };
    payControl?.addEventListener(
      PaymentController.PAYMENT_COMPLETE,
      eventHandler
    );
    return () =>
      payControl?.removeEventListener(
        PaymentController.PAYMENT_COMPLETE,
        eventHandler
      );
  }, [onPaymentComplete]);

  useEffect(() => {
    const eventHandler = (response) => {
      onScheduledPaymentCreated?.(response);
    };
    payControl?.addEventListener(
      PaymentController.SCHEDULED_PAYMENT_CREATED,
      eventHandler
    );
    return () =>
      payControl?.removeEventListener(
        PaymentController.SCHEDULED_PAYMENT_CREATED,
        eventHandler
      );
  }, [onScheduledPaymentCreated]);

  const onOpenPayment = useCallback(() => {
    setLoading(true);
    payControl?.makePayment(payment);
  }, [payment]);

  return { oneIncPortalReady, loading, onOpenPayment };
};

export const usePaymentConfig = () => {
  const userInfo = useUser();
  const { data } = usePolicyDetail();

  const { accounting, mortgagees } = data?.userBilling ?? {};
  const { dueDate, minimumPaymentDue, paymentPlan, outstandingBalanceDue } =
    accounting ?? {};

  const {
    currentTerm,
    mailingAddress,
    keystonePolicyId,
    quoteNumber,
    policyStatus,
    policyStateAttributes,
  } = data?.userDetails ?? {};
  const {
    policyNumber,
    agencyLocationCode,
    totalPremium,
    effectiveDatePolicyTerm,
  } = currentTerm ?? {};

  const agentPhone = data?.userInsuranceRep?.agentPhone ?? "";
  const insuranceRepPhone = isFormattedPhone(agentPhone)
    ? agentPhone
    : formatPhone(agentPhone);

  const [firstMortgagee] = mortgagees ?? [];

  const billingAddressRaw = getBillingAddressRaw(
    paymentPlan?.planType,
    firstMortgagee,
    mailingAddress
  );

  const paymentDueOnCancelledPolicy = acceptPaymentOnCancelledPolicy(
    policyStatus,
    policyStateAttributes?.reasonCode?.value ?? "",
    effectiveDatePolicyTerm,
    Number(minimumPaymentDue),
    data?.userPolicyProperties?.moratorium?.isUnderMoratorium
  );

  const paymentDueDate =
    dueDate === "No Upcoming Payment" || dueDate == null
      ? addDays(Date.now(), 30)
      : addDays(Date.parse(dueDate), 3);
  const tomorrow = addDays(Date.now(), 1);
  let finalPayDate: string;
  if (dueDate) {
    finalPayDate =
      paymentDueDate < tomorrow
        ? new Date(tomorrow).toISOString().split("T")[0]
        : new Date(paymentDueDate).toISOString().split("T")[0];
  } else {
    finalPayDate = "No due date";
  }

  const accountBalance = Number(
    paymentDueOnCancelledPolicy ? totalPremium : outstandingBalanceDue
  );
  const minAmountDue = Math.max(1, Number(minimumPaymentDue));

  const payConfig: PaymentConfig = {
    accountBalance,
    agencyLocationCode,
    finalPayDate,
    minAmountDue,
    billingCity: billingAddressRaw.billingCity,
    billingState: billingAddressRaw.billingState,
    billingStreet: billingAddressRaw.billingStreet,
    billingZip: billingAddressRaw.billingZip,
    email: userInfo?.email ?? "",
    firstName: userInfo?.firstName ?? "",
    insightPolicyId: keystonePolicyId ?? "",
    isScheduledPayEnabled: true,
    lastName: userInfo?.lastName ?? "",
    paymentApplicationClient: "2000",
    phone: insuranceRepPhone,
    policyId: policyNumber ?? "",
    quoteId: quoteNumber ?? "",
  };

  return payConfig;
};
